import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { useNavigate } from "react-router-dom";
import {
  mostraAlertaError,
  mostraAlertaOk,
} from "../../../componentes/alerts/sweetAlert";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from "../../../componentes/contenedores/Card";
import GestionUbicacion from "../../../componentes/googlemap/GestionUbicacion";
import VerUbicacion from "../../../componentes/googlemap/VerUbicacion";
import ModalBootstrap from "../../../componentes/modal/ModalBootstrap";
import Page from "../../../componentes/plantilla/Page";
import {
  ImagenProducto,
  PedidosGuardar
} from "../../../configuracion/apiUrls";
import { DireccionConfig } from "../../../configuracion/Empresa";
import { useContextCliente } from "../../../contexto/cliente/ClienteContext";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";

const formatoMoneda = (valor) =>
  "L " +
  new Intl.NumberFormat("en-HN", { minimumFractionDigits: 2 }).format(
    parseFloat(valor || 0)
  );

const calcularPrecioFinalConTodo = (datosProducto, cantidad) => {
  let precioCalculado = parseFloat(datosProducto.precio) || 0;
  let esMayoreoAplicado = false;
  let esPrecioDeLoteBaseAplicado = false;
  let esPrecioDeLoteCantidadAplicado = false;
  let loteId = null

  const primerLoteActivo = (datosProducto.lotes || []).find(lote => lote.estado === "Disponible" && lote.existencia > 0);

  if (primerLoteActivo) {
    precioCalculado = parseFloat(primerLoteActivo.precio) || precioCalculado;
    esPrecioDeLoteBaseAplicado = true;
    loteId = primerLoteActivo.id;

    if (Array.isArray(primerLoteActivo.loteprecios) && primerLoteActivo.loteprecios.length > 0) {
      const lotPreciosOrdenados = [...primerLoteActivo.loteprecios].sort((a, b) => b.cantidad - a.cantidad);

      for (const nivelLotePrecio of lotPreciosOrdenados) {
        if (cantidad >= nivelLotePrecio.cantidad) {
          precioCalculado = parseFloat(nivelLotePrecio.precio) || precioCalculado;
          esPrecioDeLoteCantidadAplicado = true;
          break;
        }
      }
    }
  } else {
    if (datosProducto.productoprecios && datosProducto.productoprecios.length > 0) {
      const nivelesMayoreoOrdenados = [...datosProducto.productoprecios].sort((a, b) => b.cantidad - a.cantidad);
      for (const nivel of nivelesMayoreoOrdenados) {
        if (cantidad >= nivel.cantidad) {
          precioCalculado = parseFloat(nivel.precio) || precioCalculado;
          esMayoreoAplicado = true;
          break;
        }
      }
    }
  }

  return {
    price: precioCalculado.toFixed(2),
    esMayoreoAplicado,
    esPrecioDeLoteBaseAplicado,
    esPrecioDeLoteCantidadAplicado,
    loteId
  };
};

const HomePedidos = () => {
  const { carritoPedidos, vaciarCarritoPedidos, setCarritoPedidos, usuario } = useContextUsuario();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { listaProductos = [] } = useContextCliente();
  const [productos, setProductos] = useState([]);
  const fechaActual = moment().format("dddd, DD [de] MMMM [de]YYYY");
  const [notaGeneral, setNotaGeneral] = useState("");
  const [productoSearchTerm, setProductoSearchTerm] = useState("");
  const [requiereEnvio, setRequiereEnvio] = useState(false);
  const [mostrarBotonEnvio, setMostrarBotonEnvio] = useState(false);
  const [nuevaDireccion, setNuevaDireccion] = useState({
    direccion: DireccionConfig.direccion,
    longitud: DireccionConfig.longitud,
    latitud: DireccionConfig.latitud,
  });
  const [seleccionarDireccion, setSeleccionarDireccion] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [formularioClientes, setFormularioClientes] = useState({
    direcciones: [],
  });

  const datos = {
    titulo: {
      titulo: "Nuevo pedido",
      url: "/app/clientes/pedidos/nuevo",
      tituloUrl: "pedidos",
      nombreUrl: "Inicio Pedidos",
    },
  };

  useEffect(() => {
    if (seleccionarDireccion) {
      setIsModalOpen(false);
      if (formularioClientes.direcciones.length >= 1) {
        mostraAlertaError("Solo puedes agregar una dirección por pedido.");
      } else {
        setFormularioClientes((prevState) => ({
          ...prevState,
          direcciones: [...prevState.direcciones, nuevaDireccion],
        }));
      }
      setSeleccionarDireccion(false);
    }
  }, [seleccionarDireccion]);

  const eliminarDireccion = (index) => {
    setFormularioClientes((prevState) => {
      const nuevasDirecciones = [...prevState.direcciones];
      nuevasDirecciones.splice(index, 1);
      return { ...prevState, direcciones: nuevasDirecciones };
    });
  };

  const transformarProductos = (listaProductos) =>
    Array.isArray(listaProductos)
      ? listaProductos.map((p) => {
        return {
          id: p.id,
          nombre: p.nombre,
          descripcion: p.descripcion,
          precio: parseFloat(p.precio).toFixed(2),
          existencia: p.existencia,
          codigo: p.codigo,
          imagen:
            p.productoImagens && p.productoImagens[0]?.imagen
              ? ImagenProducto + p.productoImagens[0].imagen
              : "/productos.jpg",
          productoprecios: p.productoprecios || [],
          lotes: p.lotes || [],
        };
      })
      : [];

  const agregarAlCarrito = (producto) => {
    const productoCompleto = listaProductos.find(p => p.id === producto.id);
    if (!productoCompleto) {
      mostraAlertaError("Producto no encontrado", "Error");
      return;
    }

    setCarritoPedidos((prev) => {
      const productoExistente = prev.find((item) => item.id === producto.id);
      if (productoExistente) {
        const nuevaCantidad = productoExistente.cantidad + 1;
        const { loteId, ...detallesPrecio } = calcularPrecioFinalConTodo(productoCompleto, nuevaCantidad);
        return prev.map((item) =>
          item.id === producto.id
            ? { ...item, cantidad: nuevaCantidad, loteId, ...detallesPrecio }
            : item
        );
      } else {
        const { loteId, ...detallesPrecio } = calcularPrecioFinalConTodo(productoCompleto, 1);
        return [...prev, { ...producto, cantidad: 1, loteId, ...detallesPrecio, id: producto.id }];
      }
    });
    setModalIsOpen(false);
  };

  const recalcularPrecio = (id, nuevaCantidad) => {
    const productoCompleto = listaProductos.find(p => p.id === id);
    if (!productoCompleto) return {};
    return calcularPrecioFinalConTodo(productoCompleto, nuevaCantidad);
  };

  const incrementarCantidad = (id) => {
    setCarritoPedidos((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const nuevaCantidad = item.cantidad + 1;
          const { loteId, ...detallesPrecio } = recalcularPrecio(id, nuevaCantidad);
          return { ...item, cantidad: nuevaCantidad, loteId, ...detallesPrecio };
        }
        return item;
      })
    );
  };

  const decrementarCantidad = (id) => {
    setCarritoPedidos((prev) =>
      prev.map((item) => {
        if (item.id === id && item.cantidad > 1) {
          const nuevaCantidad = item.cantidad - 1;
          const { loteId, ...detallesPrecio } = recalcularPrecio(id, nuevaCantidad);
          return { ...item, cantidad: nuevaCantidad, loteId, ...detallesPrecio };
        }
        return item;
      })
    );
  };

  const actualizarCantidad = (id, valor) => {
    setCarritoPedidos((prev) =>
      prev.map((p) => {
        if (p.id === id) {
          const nuevaCantidad = parseInt(valor, 10) || 1;
          const { loteId, ...detallesPrecio } = recalcularPrecio(id, nuevaCantidad);
          return { ...p, cantidad: nuevaCantidad, loteId, ...detallesPrecio };
        }
        return p;
      })
    );
  };

  const columnasCarrito = useMemo(() => [
    {
      title: "Eliminar / Cantidad",
      data: null,
      orderable: false,
      createdCell: (td, cellData, rowData) => {
        const root = createRoot(td);
        root.render(
          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
            <button
              className="btn btn-danger btn-md d-flex align-items-center gap-1 rounded"
              onClick={() =>
                setCarritoPedidos((prev) => prev.filter((p) => p.id !== rowData.id))
              }
            >
              <i className="fas fa-trash"></i>
            </button>
            <button
              className="btn btn-outline-primary btn-md d-flex align-items-center gap-1 rounded mx-2"
              onClick={() => decrementarCantidad(rowData.id)}
            >
              <i className="fas fa-minus"></i>
            </button>
            <input
              type="number"
              className="form-control text-center fw-bold"
              value={rowData.cantidad}
              onChange={(e) => actualizarCantidad(rowData.id, e.target.value)}
              style={{
                width: "70px",
                fontSize: "1rem",
                textAlign: "center",
              }}
            />
            <button
              className="btn btn-outline-primary btn-md d-flex align-items-center gap-1 rounded mx-2"
              onClick={() => incrementarCantidad(rowData.id)}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        );
      },
    },
    { title: "Nombre", data: "nombre" },
    {
      title: "Precio",
      data: null,
      createdCell: (td, cellData, rowData) => {
        const root = createRoot(td);
        root.render(
          <div className="text-success fw-bold">
            {formatoMoneda(rowData.price)}
            {rowData.esPrecioDeLoteCantidadAplicado && (
              <span className="badge bg-info mt-1 ms-2">Precio por Cantidad</span>
            )}
            {rowData.esMayoreoAplicado && (
              <span className="badge bg-warning mt-1 ms-2">Precio por Mayor</span>
            )}
          </div>
        );
      }
    },
    {
      title: "Subtotal",
      data: null,
      createdCell: (td, cellData, rowData) => {
        const root = createRoot(td);
        const subtotal = (parseFloat(rowData.price) * rowData.cantidad).toFixed(2);
        root.render(
          <div className="text-danger fw-bold">
            {formatoMoneda(subtotal)}
          </div>
        );
      }
    }
  ], [carritoPedidos]);

  const datosCarrito = useMemo(() => carritoPedidos.map((item) => ({ ...item, id: item.id })), [carritoPedidos]);

  const limpiarDatos = () => {
    setFormularioClientes({
      direcciones: [],
    });
    setNotaGeneral("");
    setRequiereEnvio(false);
    setMostrarBotonEnvio(false);
  };

  const guardarPedido = async () => {
    if (carritoPedidos.length === 0) {
      mostraAlertaError("No hay productos en el carrito.", "Error");
      return;
    }

    const direccionesVacia = formularioClientes.direcciones.filter(
      (f) => f.direccion !== "" || f.longitud !== null || f.latitud !== null
    );

    const detalles = carritoPedidos.map((item) => ({
      productoId: item.id,
      cantidad: item.cantidad || 1,
      precio: parseFloat(item.price) || 0,
      loteId: item.loteId || null,
    }));

    const payload = {
      ...formularioClientes,
      fecha: moment().toISOString(),
      estado: "Pendiente",
      envio: direccionesVacia.length > 0 ? "Pendiente" : "Sin envío",
      notas: notaGeneral || "Sin notas",
      clienteId: usuario.datoPersonales.id,
      detalles,
      direcciones: direccionesVacia,
    };

    try {
      await AxiosPublico.post(PedidosGuardar, payload);
      mostraAlertaOk("Pedido guardado correctamente.", "Éxito");
      vaciarCarritoPedidos();
      limpiarDatos();
    } catch (error) {
      mostraAlertaError("Error al guardar el pedido.", "Error");
    }
  };

  useEffect(() => {
    const productosTransformados = transformarProductos(listaProductos);
    setProductos(productosTransformados);
  }, [listaProductos]);

  const productosFiltrados = productos.filter((producto) =>
    (producto.nombre + producto.codigo)
      .toLowerCase()
      .includes(productoSearchTerm.toLowerCase())
  );

  const verCatalogo = () => {
    navigate("/app/clientes/productos");
  };

  const [paginaActual, setPaginaActual] = useState(1);
  const productosPorPagina = 5;
  const indiceUltimo = paginaActual * productosPorPagina;
  const indicePrimero = indiceUltimo - productosPorPagina;
  const productosPagina = productosFiltrados.slice(indicePrimero, indiceUltimo);
  const totalPaginas = Math.ceil(productosFiltrados.length / productosPorPagina);
  const totalPedido = useMemo(() => {
    return carritoPedidos.reduce((sum, item) => {
      return sum + (parseFloat(item.price) * item.cantidad);
    }, 0).toFixed(2);
  }, [carritoPedidos]);

  return (
    <Page datos={datos}>
      <Card
        pie={<>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center w-100 gap-2 mb-3 mb-md-0">
            <button className="btn btn-success flex-grow-1 flex-md-grow-0" onClick={guardarPedido}>
              <i className="fas fa-save me-2 mx-2" /> Guardar
            </button>
            <h5 className="mb-2 mb-md-0 text-md-end text-center mt-3 mt-md-0">Total del Pedido:
              <span className="text-success mx-2 fw-bold">{formatoMoneda(totalPedido)}
              </span>
            </h5>
          </div>
        </>}
      >
        <h3 className="text-muted mb-3 text-center text-md-start">{fechaActual}</h3>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
          <ModalBootstrap
            titulo="Agregar Productos"
            nombreBoton="Agregar producto"
            estiloBoton="btn btn-primary mb-2 mb-md-0"
            iconBoton="fas fa-cart-plus"
            modalIsOpen={modalIsOpen}
            setModalIsOpen={setModalIsOpen}
          >
            <div className="mb-3 d-flex justify-content-center">
              <div className="col-12 col-md-8 col-lg-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar producto por nombre o código..."
                  value={productoSearchTerm}
                  onChange={(e) => setProductoSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered table-striped table-hover shadow-sm">
                <thead className="bg-dark text-white">
                  <tr>
                    <th>Imagen</th>
                    <th>Detalles del Producto</th>
                    <th>Agregar</th>
                  </tr>
                </thead>
                <tbody>
                  {productosPagina.length > 0 ? (
                    productosPagina.map((producto) => (
                      <tr key={producto.id}>
                        <td className="text-center align-middle">
                          <img
                            src={producto.imagen}
                            alt={producto.nombre}
                            className="img-thumbnail"
                            style={{ width: "80px", height: "80px", objectFit: "cover" }}
                          />
                        </td>
                        <td className="align-middle">
                          <div className="d-flex flex-column">
                            <div className="fw-bold">{producto.nombre}</div>
                            <small className="text-muted mb-2">{producto.codigo}</small>
                            {(() => {
                              const { price, esPrecioDeLoteBaseAplicado, esPrecioDeLoteCantidadAplicado, esMayoreoAplicado } = calcularPrecioFinalConTodo(producto, 1);
                              const primerLoteActivo = (producto.lotes || []).find(lote => lote.estado === "Disponible" && lote.existencia > 0);
                              return (
                                <div>
                                  <span className="text-success fw-bold">{formatoMoneda(price)}</span>
                                  {esPrecioDeLoteCantidadAplicado && (
                                    <span className="badge bg-info mt-1 ms-2">Precio por Cantidad</span>
                                  )}
                                  {esMayoreoAplicado && (
                                    <span className="badge bg-warning mt-1 ms-2">Precio por Mayor</span>
                                  )}
                                  {primerLoteActivo && primerLoteActivo.loteprecios && primerLoteActivo.loteprecios.length > 0 && (
                                    <div className="mt-2">
                                      <p className="small mb-0 text-muted">Precios por Cantidad (Lote):</p>
                                      <ul className="list-unstyled small">
                                        {primerLoteActivo.loteprecios
                                          .sort((a, b) => a.cantidad - b.cantidad)
                                          .map((tier, idx) => (
                                            <li key={idx}>
                                              {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                            </li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                                  {!esPrecioDeLoteBaseAplicado && !esPrecioDeLoteCantidadAplicado && producto.productoprecios && producto.productoprecios.length > 0 && (
                                    <div className="mt-2">
                                      <p className="small mb-0 text-muted">Precios por Mayor (Producto):</p>
                                      <ul className="list-unstyled small">
                                        {producto.productoprecios
                                          .sort((a, b) => a.cantidad - b.cantidad)
                                          .map((tier, idx) => (
                                            <li key={idx}>
                                              {tier.cantidad}+ unid : {formatoMoneda(tier.precio)}
                                            </li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="text-center align-middle">
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success btn-sm mx-2"
                              onClick={() => agregarAlCarrito(producto)}
                            >
                              <i className="fa-solid fa-cart-plus" />
                            </button>
                            <button
                              className="btn btn-info btn-sm d-flex align-items-center text-white"
                              onClick={verCatalogo}
                            >
                              <i className="fa-solid fa-store me-1"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-muted">
                        No se encontraron productos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <nav className="mt-4">
              <ul className="pagination pagination-sm justify-content-center shadow-sm bg-light p-2 rounded">
                <li className={`page-item ${paginaActual === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPaginaActual(paginaActual - 1)}
                    aria-label="Anterior"
                  >
                    <i className="fas fa-chevron-left me-1"></i> Anterior
                  </button>
                </li>
                {[...Array(totalPaginas)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${paginaActual === i + 1 ? "active" : ""}`}
                  >
                    <button className="page-link fw-bold" onClick={() => setPaginaActual(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${paginaActual === totalPaginas ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPaginaActual(paginaActual + 1)}
                    aria-label="Siguiente"
                  >
                    Siguiente <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </ModalBootstrap>
        </div>
        <div className="mb-3">
          {carritoPedidos?.length > 0 ? (
            <div className="d-flex flex-column gap-3">
              {carritoPedidos.map((item) => {
                const subtotal = (parseFloat(item.price) * item.cantidad).toFixed(2);
                return (
                  <div key={item.id} className="card shadow-sm p-3 border-secondary">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={item.imagen}
                          alt={item.nombre}
                          className="img-thumbnail"
                          style={{ width: "80px", height: "80px", objectFit: "cover" }}
                        />
                        <div className="flex-grow-1 mx-2">
                          <h5 className="mb-1">{item.nombre}</h5>
                          <div className="text-muted small">
                            {formatoMoneda(item.price)} x {item.cantidad} unidades
                          </div>
                          <div className="fw-bold text-success mt-1 d-md-none">
                            Subtotal: {formatoMoneda(subtotal)}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-grow-1 flex-md-grow-0 align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mt-md-0 w-100 w-md-auto">
                        <div className="fw-bold text-success d-none d-md-block text-nowrap mx-2">
                          Subtotal: {formatoMoneda(subtotal)}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => decrementarCantidad(item.id)}
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                          <input
                            type="number"
                            className="form-control mx-2"
                            value={item.cantidad}
                            onChange={(e) => actualizarCantidad(item.id, e.target.value)}
                            style={{ width: "85px" }}
                          />
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => incrementarCantidad(item.id)}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                        <button
                          className="btn btn-danger btn-sm mx-2"
                          onClick={() => setCarritoPedidos((prev) => prev.filter((p) => p.id !== item.id))}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <h6 className="text-center">No tienes productos en tu carrito</h6>
          )}
        </div>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="switchEnvio"
            checked={requiereEnvio}
            onChange={(e) => {
              setRequiereEnvio(e.target.checked);
              setMostrarBotonEnvio(e.target.checked);
            }}
          />
          <label className="form-check-label" htmlFor="switchEnvio">
            ¿Este pedido requiere envío?
          </label>
        </div>

        {mostrarBotonEnvio && (
          <>
            <div className="row mb-3">
              <div className="col-12 col-md-6">
                <label className="fw-bold">
                  <i className="fas fa-truck text-primary me-2"></i>
                  <span className="mx-2">Cobro extra por envío</span>
                </label>
                <p className="text-muted small">
                  Al seleccionar envío, se añadirá un costo adicional para cubrir el transporte y la entrega del pedido.
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <label className="fw-bold">Direccion del envio</label>
                <div className="pb-3">
                  <GestionUbicacion
                    direccion={nuevaDireccion}
                    setDireccion={setNuevaDireccion}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    setSeleccionarDireccion={setSeleccionarDireccion}
                    textoBoton={"Agregar Ubicación"}
                    verdireccion={false}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <div className="form-group">
                  {formularioClientes.direcciones
                    .filter((f) => f.direccion && f.latitud && f.longitud)
                    .map((f, index) => (
                      <div key={index} className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2 p-2 border rounded">
                        <div className="mb-2 mb-md-0">
                          <p className="text-muted mb-0">{f.direccion}</p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <VerUbicacion direccion={f} />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => eliminarDireccion(index)}
                            title="Eliminar dirección"
                          >
                            <i className="fa fa-minus" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </>
        )}
        <div className="mb-3">
          <h2 className="fw-bold small">
            Al seleccionar envío, puede agregar un poco mas detallado en notas pedidos como referencia de direccion.
          </h2>
          <label className="form-label fw-bold">
            <i className="fas fa-sticky-note me-1"></i> Nota del Pedido o Referencia de la direccion pedido:
          </label>
          <textarea
            id="notaGeneral"
            className="form-control border-primary rounded shadow-sm"
            placeholder="Agrega una nota general..."
            value={notaGeneral}
            onChange={(e) => setNotaGeneral(e.target.value)}
          ></textarea>
        </div>
      </Card>
    </Page>
  );
};

export default HomePedidos;